import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ArrowLeft, Clock, Phone, Mail, Globe, MapPin, Instagram, Facebook, Share2, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Separator } from "../components/ui/separator";
import { Seo } from "../components/Seo";
import { isOpenNow, buildOpeningHoursSpecification } from "../lib/hours";
import { toast } from "sonner";
import { API } from "../App";

export default function TenantDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTenant = async () => {
      try {
        const res = await axios.get(`${API}/tenants/${id}`);
        setTenant(res.data);
      } catch (e) {
        toast.error("Store not found");
        navigate("/directory");
      } finally {
        setLoading(false);
      }
    };
    fetchTenant();
  }, [id, navigate]);

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: tenant.name,
          text: `Check out ${tenant.name} at Nyali Plaza`,
          url: window.location.href,
        });
      } catch (e) {
        // User cancelled the share sheet
      }
    } else {
      navigator.clipboard.writeText(window.location.href);
      toast.success("Link copied to clipboard!");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!tenant) return null;

  return (
    <div className="min-h-screen bg-background">
      <Seo
        title={tenant.name}
        path={`/tenant/${tenant.slug || tenant.id}`}
        description={
          tenant.description
            ? `${tenant.description.slice(0, 155)}`
            : `${tenant.name} — ${tenant.category} at Nyali Plaza, Mombasa. Find contact details, opening hours and directions.`
        }
        image={tenant.image_url || undefined}
        jsonLd={[{
          "@context": "https://schema.org",
          "@type": "LocalBusiness",
          name: tenant.name,
          description: tenant.description || `${tenant.category} at Nyali Plaza, Mombasa.`,
          image: tenant.image_url || undefined,
          url: `https://nyaliplazaltd.com/tenant/${tenant.slug || tenant.id}`,
          telephone: tenant.contacts?.[0]?.phone || undefined,
          email: tenant.email || undefined,
          address: {
            "@type": "PostalAddress",
            streetAddress: "Nyali Plaza, Main Nyali Road",
            addressLocality: "Mombasa",
            addressCountry: "KE",
          },
          openingHoursSpecification: buildOpeningHoursSpecification(tenant.business_hours),
        }, {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://nyaliplazaltd.com/" },
            { "@type": "ListItem", position: 2, name: "Directory", item: "https://nyaliplazaltd.com/directory" },
            { "@type": "ListItem", position: 3, name: tenant.name, item: `https://nyaliplazaltd.com/tenant/${tenant.slug || tenant.id}` },
          ],
        }]}
      />
      {/* Hero Image */}
      <div className="relative h-[40vh] min-h-[280px] max-h-[400px]">
        <img
          src={tenant.image_url || "https://images.unsplash.com/photo-1635930376155-4ee50db13cca?w=800"}
          alt={tenant.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        
        {/* Back Button */}
        <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between">
          <Link to="/directory" data-testid="detail-back-button">
            <Button variant="ghost" size="icon" className="rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white">
              <ArrowLeft className="w-5 h-5" />
            </Button>
          </Link>
          <Button variant="ghost" size="icon" className="rounded-full bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white" onClick={handleShare} data-testid="share-button">
            <Share2 className="w-5 h-5" />
          </Button>
        </div>
      </div>

      {/* Content Sheet */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="relative -mt-12 rounded-t-3xl bg-background"
      >
        <div className="max-w-md mx-auto md:max-w-2xl px-4 py-6">
          {/* Header */}
          <div className="mb-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${isOpenNow(tenant.business_hours) ? 'badge-open' : 'badge-closed'}`}>
                    <Clock className="w-3 h-3" />
                    {isOpenNow(tenant.business_hours) ? 'Open Now' : 'Closed'}
                  </span>
                  {tenant.is_anchor && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-secondary/20 text-secondary-foreground text-xs font-bold uppercase">
                      Anchor Tenant
                    </span>
                  )}
                </div>
                <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground" data-testid="tenant-name">{tenant.name}</h1>
                <p className="text-muted-foreground mt-1">{tenant.category}</p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-3 gap-3 mb-6">
            {tenant.contacts?.[0]?.phone && (
              <a href={`tel:${tenant.contacts[0].phone.replace(/\s/g, '')}`} data-testid="call-button">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2 rounded-2xl">
                  <Phone className="w-5 h-5 text-primary" />
                  <span className="text-xs">Call</span>
                </Button>
              </a>
            )}
            {tenant.contacts?.[0]?.whatsapp && (
              <a href={`https://wa.me/${tenant.contacts[0].whatsapp.replace(/\s/g, '').replace('+', '')}`} target="_blank" rel="noopener noreferrer" data-testid="whatsapp-button">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2 rounded-2xl">
                  <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                  <span className="text-xs">WhatsApp</span>
                </Button>
              </a>
            )}
            {tenant.social_media?.website && (
              <a href={tenant.social_media.website.startsWith('http') ? tenant.social_media.website : `https://${tenant.social_media.website}`} target="_blank" rel="noopener noreferrer" data-testid="website-button">
                <Button variant="outline" className="w-full h-auto py-4 flex flex-col items-center gap-2 rounded-2xl">
                  <Globe className="w-5 h-5 text-primary" />
                  <span className="text-xs">Website</span>
                </Button>
              </a>
            )}
          </div>

          <Separator className="my-6" />

          {/* Description */}
          {tenant.description && (
            <div className="mb-6">
              <h3 className="font-heading font-semibold text-foreground mb-2">About</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{tenant.description}</p>
            </div>
          )}

          {/* Offers */}
          {tenant.offers && tenant.offers.length > 0 && (
            <div className="mb-6">
              <h3 className="font-heading font-semibold text-foreground mb-3">What We Offer</h3>
              <div className="flex flex-wrap gap-2">
                {tenant.offers.map((offer) => (
                  <span key={offer} className="px-3 py-1.5 rounded-full bg-accent text-accent-foreground text-sm">
                    {offer}
                  </span>
                ))}
              </div>
            </div>
          )}

          <Separator className="my-6" />

          {/* Business Hours */}
          {tenant.business_hours && (
            <div className="mb-6">
              <h3 className="font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Business Hours
              </h3>
              <div className="bg-muted rounded-2xl p-4 space-y-2">
                {tenant.business_hours.weekdays && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Mon - Fri</span>
                    <span className="font-medium text-foreground">{tenant.business_hours.weekdays}</span>
                  </div>
                )}
                {tenant.business_hours.saturday && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Saturday</span>
                    <span className="font-medium text-foreground">{tenant.business_hours.saturday}</span>
                  </div>
                )}
                {tenant.business_hours.sunday && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Sunday</span>
                    <span className="font-medium text-foreground">{tenant.business_hours.sunday}</span>
                  </div>
                )}
                {tenant.business_hours.note && (
                  <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border">{tenant.business_hours.note}</p>
                )}
              </div>
            </div>
          )}

          {/* Contact Info */}
          {(tenant.contacts?.length > 0 || tenant.email) && (
            <div className="mb-6">
              <h3 className="font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                Contact
              </h3>
              <div className="space-y-3">
                {tenant.contacts?.map((contact, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-muted rounded-xl p-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">{contact.name || 'Contact'}</p>
                      <p className="text-sm text-muted-foreground">{contact.phone}</p>
                    </div>
                    <div className="flex gap-2">
                      <a href={`tel:${contact.phone.replace(/\s/g, '')}`}>
                        <Button variant="ghost" size="icon" className="rounded-full h-9 w-9">
                          <Phone className="w-4 h-4" />
                        </Button>
                      </a>
                      {contact.whatsapp && (
                        <a href={`https://wa.me/${contact.whatsapp.replace(/\s/g, '').replace('+', '')}`} target="_blank" rel="noopener noreferrer">
                          <Button variant="ghost" size="icon" className="rounded-full h-9 w-9 text-green-600">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/></svg>
                          </Button>
                        </a>
                      )}
                    </div>
                  </div>
                ))}
                {tenant.email && (
                  <a href={`mailto:${tenant.email}`} className="flex items-center gap-3 bg-muted rounded-xl p-3 hover:bg-muted/80 transition-colors" data-testid="email-link">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-foreground">{tenant.email}</span>
                    <ExternalLink className="w-3 h-3 text-muted-foreground ml-auto" />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Location */}
          {tenant.location && (
            <div className="mb-6">
              <h3 className="font-heading font-semibold text-foreground mb-3 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Location
              </h3>
              <div className="bg-muted rounded-xl p-3">
                <p className="text-sm text-foreground">{tenant.location}</p>
                <p className="text-xs text-muted-foreground mt-1">Nyali Plaza, Mombasa</p>
              </div>
            </div>
          )}

          {/* Social Media */}
          {tenant.social_media && (tenant.social_media.instagram || tenant.social_media.facebook || tenant.social_media.tiktok) && (
            <div className="mb-6">
              <h3 className="font-heading font-semibold text-foreground mb-3">Follow Us</h3>
              <div className="flex gap-3">
                {tenant.social_media.instagram && (
                  <a href={`https://instagram.com/${tenant.social_media.instagram.replace('@', '')}`} target="_blank" rel="noopener noreferrer" data-testid="instagram-link">
                    <Button variant="outline" size="icon" className="rounded-full">
                      <Instagram className="w-4 h-4" />
                    </Button>
                  </a>
                )}
                {tenant.social_media.facebook && (
                  <a href={`https://facebook.com/${tenant.social_media.facebook.replace('@', '')}`} target="_blank" rel="noopener noreferrer" data-testid="facebook-link">
                    <Button variant="outline" size="icon" className="rounded-full">
                      <Facebook className="w-4 h-4" />
                    </Button>
                  </a>
                )}
                {tenant.social_media.tiktok && (
                  <a href={`https://tiktok.com/${tenant.social_media.tiktok}`} target="_blank" rel="noopener noreferrer" data-testid="tiktok-link">
                    <Button variant="outline" size="icon" className="rounded-full">
                      <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/></svg>
                    </Button>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Back to Directory */}
          <div className="pt-4 pb-8">
            <Link to="/directory" data-testid="back-to-directory">
              <Button variant="outline" className="w-full rounded-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Directory
              </Button>
            </Link>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
